import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import clsx from 'clsx';
import SectionEditActions from './SectionEditActions';
import TenantReservedSpots from './TenantReservedSpots';

const FIELD_BORDER = '#F7F8FA';
const FIELD_BG = '#F7F8FC';
// Darker border only when editable (fill stays the same)
const FIELD_BORDER_EDIT = '#D0D5DD';

const fieldOutline = {
  borderColor: `${FIELD_BORDER} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const fieldOutlineEdit = {
  borderColor: `${FIELD_BORDER_EDIT} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const useStyles = makeStyles((theme) => ({
  // Native fieldset/legend gaps the top border under the title (transparent OK)
  card: {
    margin: 0,
    // Match VehiclesSection gap (`<Box mb={2} />` between tenant / guest)
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: 0,
    minWidth: 0,
    border: '1px solid #E4E7EC',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#FFFFFF',
  },
  cardTitle: {
    marginLeft: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: 'transparent',
    color: '#003E7E',
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  cardBody: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  countsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  countGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  countLabel: {
    marginRight: theme.spacing(1.5),
  },
  // Match Reserved Spot / count labels (subtitle2 primary)
  fieldLabel: {
    marginBottom: theme.spacing(0.25),
    fontSize: '0.8125rem',
  },
  countBox: {
    minWidth: 72,
    height: 32,
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: FIELD_BG,
  },
  // Always muted — counts are display-only (never Contact-Edit editable)
  countValue: {
    fontWeight: 400,
    color: '#9AA0A6',
    lineHeight: 1,
  },
  actions: {
    marginLeft: 'auto',
  },
  // View mode — same muted soft box as Issue date (readOnly, not HTML disabled,
  // so react-hook-form still includes values on Contact Save).
  contactField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: `${FIELD_BG} !important`,
      backgroundImage: 'none',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent !important',
      backgroundImage: 'none',
      color: '#9AA0A6',
      WebkitTextFillColor: '#9AA0A6',
      cursor: 'default',
    },
    '& .MuiOutlinedInput-input[readonly]': {
      backgroundColor: 'transparent !important',
      color: '#9AA0A6',
      WebkitTextFillColor: '#9AA0A6',
      cursor: 'default',
    },
    '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutline,
    '& .MuiOutlinedInput-input[readonly] + .MuiOutlinedInput-notchedOutline': fieldOutline,
  },
  // EDIT unlocked — normal text + slightly darker outline
  contactFieldEditing: {
    '& .MuiOutlinedInput-input': {
      color: 'inherit',
      WebkitTextFillColor: 'inherit',
      cursor: 'text',
    },
    '& .MuiOutlinedInput-input[readonly]': {
      color: 'inherit',
      WebkitTextFillColor: 'inherit',
      cursor: 'text',
    },
    '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiOutlinedInput-root:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
    '& .MuiOutlinedInput-root:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': fieldOutlineEdit,
  },
}));

const ContactTextField = ({
  label,
  name,
  inputRefs,
  fieldErrors,
  isEditing,
  displayValue,
}) => {
  const classes = useStyles();
  const error = fieldErrors?.[name];

  return (
    <Grid item xs={12} md={6} lg={3}>
      <Typography
        variant="subtitle2"
        color="primary"
        className={classes.fieldLabel}
      >
        {label}
      </Typography>
      <TextField
        className={clsx(
          classes.contactField,
          isEditing && classes.contactFieldEditing
        )}
        name={name}
        size="small"
        inputRef={inputRefs}
        defaultValue={displayValue ?? ''}
        error={!!error}
        helperText={error || null}
        fullWidth
        InputProps={{
          readOnly: !isEditing,
        }}
      />
    </Grid>
  );
};

// Display-only — never editable in Contact Edit. Counts change via Add Permit
// slot APIs (live local update) and reload from unit/contact GET.
const CountDisplay = ({ label, name, value }) => {
  const classes = useStyles();
  const display = value === null || value === undefined || value === '' ? '—' : value;

  return (
    <Box className={classes.countGroup}>
      <Typography
        variant="subtitle2"
        color="primary"
        className={classes.countLabel}
      >
        {label}
      </Typography>
      <Box
        className={classes.countBox}
        aria-readonly="true"
        data-testid={`${name}_display`}
      >
        <Typography
          id={`${name}_display`}
          variant="body1"
          className={classes.countValue}
        >
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

const UnitsContactInformationBlock = ({
  inputRefs,
  fieldErrors,
  disabled,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  showActions,
  submitDisabled,
  reservedSpots,
  setReservedSpots,
  getValues,
  setValue,
  watch,
  isUnitLoaded,
  tenantVehiclesNumber,
  guestVehiclesNumber,
}) => {
  const classes = useStyles();
  const watched = typeof watch === 'function' ? watch : () => undefined;
  // Contact Edit only unlocks these four fields (+ reserved spots when allowed).
  const firstName = watched('first_name') ?? getValues?.('first_name');
  const lastName = watched('last_name') ?? getValues?.('last_name');
  const phoneNumber = watched('phone_number') ?? getValues?.('phone_number');
  const email = watched('email') ?? getValues?.('email');

  return (
    <Box component="fieldset" className={classes.card}>
      <Typography
        component="legend"
        variant="h6"
        className={classes.cardTitle}
      >
        Contact Information
      </Typography>
      <Box className={classes.cardBody}>
        <Grid
          container
          direction="row"
          spacing={2}
          key={`contact-fields-${isEditing ? 'edit' : 'view'}-${isUnitLoaded ? 'loaded' : 'loading'}`}
        >
          <ContactTextField
            label="Name"
            name="first_name"
            inputRefs={inputRefs}
            fieldErrors={fieldErrors}
            isEditing={isEditing}
            displayValue={firstName}
          />
          <ContactTextField
            label="Last Name"
            name="last_name"
            inputRefs={inputRefs}
            fieldErrors={fieldErrors}
            isEditing={isEditing}
            displayValue={lastName}
          />
          <ContactTextField
            label="Phone Number"
            name="phone_number"
            inputRefs={inputRefs}
            fieldErrors={fieldErrors}
            isEditing={isEditing}
            displayValue={phoneNumber}
          />
          <ContactTextField
            label="Email"
            name="email"
            inputRefs={inputRefs}
            fieldErrors={fieldErrors}
            isEditing={isEditing}
            displayValue={email}
          />
        </Grid>

        <Box className={classes.countsRow}>
          <CountDisplay
            label="Permitted Tenant Vehicles:"
            name="tenant_vehicles_number"
            value={tenantVehiclesNumber}
          />
          <CountDisplay
            label="Permitted Daily Guest Vehicles:"
            name="daily_guest_vehicles_number"
            value={guestVehiclesNumber}
          />
          <TenantReservedSpots
            register={inputRefs}
            fieldErrors={fieldErrors}
            reservedSpots={reservedSpots}
            setReservedSpots={setReservedSpots}
            isEditMode={!disabled}
            setValue={setValue}
            getValues={getValues}
          />
          <Box className={classes.actions}>
            <SectionEditActions
              isEditing={isEditing}
              onEdit={onEdit}
              onCancel={onCancel}
              onSave={onSave}
              showActions={showActions}
              submitDisabled={submitDisabled}
              editButtonId="contact_edit_button"
              saveButtonId="contact_save_button"
              cancelButtonId="contact_cancel_button"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UnitsContactInformationBlock;
